import type { App } from './App';
import type { GameMode } from '../types';
import { getSettings, saveSettings } from '../storage/settings';
import { getAudioCtx, peekAudioCtx, resumeAudioCtx } from '../audio/ctx';

interface ModeCard {
  mode: GameMode;
  icon: string;
  title: string;
  desc: string;
  color: string;
  freq: [number, number];
}

const MODES: ModeCard[] = [
  {
    mode: 'single',
    icon: '/assets/icons/single-pick.png',
    title: '한 명 뽑기',
    desc: '발표자 · 청소당번 · 심부름',
    color: '#5EEAD4',
    freq: [880, 1100],
  },
  {
    mode: 'multi',
    icon: '/assets/icons/multi-pick.png',
    title: '여러 명 뽑기',
    desc: '상품 추첨 · 간식 분배',
    color: '#FBBF24',
    freq: [660, 880],
  },
  {
    mode: 'team',
    icon: '/assets/icons/team-split.png',
    title: '팀 나누기',
    desc: '모둠 활동 · 조별 과제',
    color: '#FB7185',
    freq: [780, 1000],
  },
  {
    mode: 'reward',
    icon: '/assets/icons/reward-roulette.png',
    title: '보상 룰렛',
    desc: '수업 동기부여 보상 게임',
    color: '#A78BFA',
    freq: [1000, 1300],
  },
];

export class HomeScreen {
  private el: HTMLElement;
  private app: App;
  private settingsOverlay: HTMLElement | null = null;

  constructor(container: HTMLElement, app: App) {
    this.app = app;
    this.el = document.createElement('div');
    this.el.className = 'home-screen';
    container.appendChild(this.el);
    this.render();
  }

  private getSoundIcon(): string {
    return getSettings().soundEnabled ? '🔊' : '🔇';
  }

  private render() {
    this.el.innerHTML = `
      <header class="home-header">
        <div class="header-brand">
          <img class="brand-icon-img" src="/assets/favicon.png" alt="" />
          <h1 class="brand-title">굴려굴려뽑기</h1>
        </div>
        <div class="header-actions">
          <button class="btn-icon" id="btn-guide" title="사용 가이드" aria-label="사용 가이드">
            <span class="icon">❓</span>
          </button>
          <button class="btn-icon" id="btn-open-class" title="학급 관리" aria-label="학급 관리">
            <span class="icon">📋</span>
          </button>
          <button class="btn-icon" id="btn-sound" title="소리 켜기/끄기" aria-label="소리 켜기/끄기">
            <span class="icon" id="sound-icon">${this.getSoundIcon()}</span>
          </button>
          <button class="btn-icon" id="btn-settings" title="설정" aria-label="설정">
            <span class="icon">⚙</span>
          </button>
          <button class="btn-icon" id="btn-theme" title="테마 전환" aria-label="테마 전환">
            <span class="icon">🌙</span>
          </button>
          <button class="btn-icon" id="btn-fullscreen" title="전체화면 (F)" aria-label="전체화면">
            <span class="icon">⛶</span>
          </button>
        </div>
      </header>

      <main class="home-main">
        <div class="mode-grid">
          ${MODES.map((m) => `
            <button class="mode-card" data-mode="${m.mode}" style="--card-color:${m.color}">
              <div class="mode-card-art">
                <span class="mode-icon-wrap">
                  <img class="mode-icon-img" src="${m.icon}" alt="" />
                </span>
              </div>
              <div class="mode-card-info">
                <strong class="mode-title">${m.title}</strong>
                <span class="mode-desc">${m.desc}</span>
              </div>
            </button>
          `).join('')}
        </div>
      </main>

      <footer class="home-footer">
        <span>Copyright &copy; 2026<span id="current-year">${new Date().getFullYear() > 2026 ? '-' + new Date().getFullYear() : ''}</span> SsamFlow. All rights reserved.</span>
        <span class="sep">·</span>
        <a href="https://github.com/lazygyu/roulette" target="_blank" rel="noopener">Based on Marble Roulette by lazygyu (MIT)</a>
        <span class="sep">·</span>
        <a href="https://ssamflow.com/legal/gulgul-pick/terms" data-sf-legal="terms">이용약관</a>
        <span class="sep">·</span>
        <a href="https://ssamflow.com/legal/gulgul-pick/privacy" data-sf-legal="privacy">개인정보 처리방침</a>
      </footer>
    `;

    this.bindEvents();
  }

  private peekCtx(): AudioContext | null {
    if (!getSettings().soundEnabled) return null;
    return peekAudioCtx();
  }

  private clickCtx(): AudioContext | null {
    if (!getSettings().soundEnabled) return null;
    resumeAudioCtx();
    return getAudioCtx();
  }

  private playHoverSound(freqLo: number, freqHi: number) {
    const ctx = this.peekCtx();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freqLo, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(freqHi, ctx.currentTime + 0.09);
    gain.gain.setValueAtTime(0.06, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.11);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.12);
  }

  private playClickSound() {
    const ctx = this.clickCtx();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.14);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.15);
  }

  private bindEvents() {
    this.el.querySelector('#btn-theme')?.addEventListener('click', () => {
      this.app.toggleTheme();
    });

    this.el.querySelector('#btn-settings')?.addEventListener('click', () => {
      this.showSettings();
    });

    this.el.querySelector('#btn-guide')?.addEventListener('click', () => {
      this.app.navigate('guide');
    });

    this.el.querySelector('#btn-open-class')?.addEventListener('click', () => {
      this.app.navigate('class-mgmt');
    });

    this.el.querySelector('#btn-sound')?.addEventListener('click', () => {
      const next = saveSettings({ soundEnabled: !getSettings().soundEnabled });
      const iconEl = this.el.querySelector('#sound-icon');
      if (iconEl) iconEl.textContent = next.soundEnabled ? '🔊' : '🔇';
      if (next.soundEnabled) this.playHoverSound(880, 1100);
    });

    this.el.querySelector('#btn-fullscreen')?.addEventListener('click', () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => { });
      } else {
        document.exitFullscreen().catch(() => { });
      }
    });

    this.el.querySelectorAll<HTMLButtonElement>('.mode-card').forEach((btn) => {
      const mode = btn.dataset.mode as GameMode;
      const card = MODES.find((m) => m.mode === mode)!;

      btn.addEventListener('mouseenter', () => {
        this.playHoverSound(card.freq[0], card.freq[1]);
      });

      btn.addEventListener('click', () => {
        this.playClickSound();
        this.app.navigate('roster', { mode });
      });
    });
  }

  private showSettings() {
    this.settingsOverlay?.remove();
    this.settingsOverlay = document.createElement('div');
    this.settingsOverlay.className = 'settings-overlay';
    this.settingsOverlay.innerHTML = `
      <div class="settings-modal">
        <div class="modal-header">
          <h3>설정</h3>
          <button class="btn-icon-sm" id="btn-close-settings">✕</button>
        </div>
        <div class="settings-list">
          <button class="settings-row" id="btn-settings-class">
            <span class="settings-icon">📋</span>
            <span>
              <strong>학급 설정</strong>
              <small>명단 추가, 가져오기, CSV 내보내기</small>
            </span>
          </button>
          <button class="settings-row" id="btn-settings-license">
            <span class="settings-icon">⚖</span>
            <span>
              <strong>MIT 라이선스</strong>
              <small>원작 Marble Roulette 라이선스</small>
            </span>
          </button>
          <button class="settings-row" id="btn-settings-map">
            <span class="settings-icon">🛠</span>
            <span>
              <strong>맵 커스텀</strong>
              <small>루트, 장애물, 아이템 배치 편집</small>
            </span>
          </button>
          <a class="settings-row" href="https://ssamflow.com" target="_blank" rel="noopener">
            <span class="settings-icon">↗</span>
            <span>
              <strong>다른 프로그램</strong>
              <small>SsamFlow 도구 모음</small>
            </span>
          </a>
        </div>
      </div>
    `;
    document.body.appendChild(this.settingsOverlay);
    this.settingsOverlay.querySelector('#btn-close-settings')?.addEventListener('click', () => this.closeSettings());
    this.settingsOverlay.addEventListener('click', (e) => {
      if (e.target === this.settingsOverlay) this.closeSettings();
    });
    this.settingsOverlay.querySelector('#btn-settings-class')?.addEventListener('click', () => {
      this.closeSettings();
      this.app.navigate('class-mgmt');
    });
    this.settingsOverlay.querySelector('#btn-settings-license')?.addEventListener('click', () => {
      this.showLicense();
    });
    this.settingsOverlay.querySelector('#btn-settings-map')?.addEventListener('click', () => {
      this.closeSettings();
      this.app.navigate('map-custom');
    });
  }

  private showLicense() {
    if (!this.settingsOverlay) return;
    this.settingsOverlay.innerHTML = `
      <div class="settings-modal license-modal">
        <div class="modal-header">
          <button class="btn-back-sm" id="btn-license-back">←</button>
          <h3>MIT 라이선스</h3>
          <button class="btn-icon-sm" id="btn-close-settings">✕</button>
        </div>
        <div class="license-body">
          <p class="license-note">
            원작: lazygyu의 Marble Roulette (MIT). This program is freeware and may be used freely anywhere, including in broadcasts and videos.
          </p>
          <pre>MIT License

Copyright (c) 2023 LazyGyu

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.</pre>
          <a href="https://github.com/lazygyu/roulette" target="_blank" rel="noopener">원본 GitHub 저장소 열기</a>
        </div>
      </div>
    `;
    this.settingsOverlay.querySelector('#btn-license-back')?.addEventListener('click', () => this.showSettings());
    this.settingsOverlay.querySelector('#btn-close-settings')?.addEventListener('click', () => this.closeSettings());
  }

  private closeSettings() {
    this.settingsOverlay?.remove();
    this.settingsOverlay = null;
  }

  destroy() {
    this.closeSettings();
    this.el.remove();
  }
}
